/* eslint-disable import/no-extraneous-dependencies */
import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import { useLocation } from 'react-router-dom';
import L from 'leaflet';
import { useStore } from '../../../../../store';
import { useMapContext } from '../../context/map-context';

const SINGLE_POINT_ZOOM = 19;
const PADDING: [number, number] = [40, 40];

export const FitBoundsToMarkers = (): null => {
  const map = useMap();
  const { mapOrigin, mapReport } = useStore();
  const { rangePerDegree, savedViewState, facilityKey } = useMapContext();
  const { search } = useLocation();
  const lastFitKeyRef = useRef<string | null>(null);
  const suppressedForRef = useRef<string | null>(null);

  useEffect(() => {
    if (mapReport.loading || mapOrigin.loading) return;
    if (!rangePerDegree?.lat || !rangePerDegree?.lon) return;

    const lat0 = Number(mapOrigin.data.latitude);
    const lon0 = Number(mapOrigin.data.longitude);
    if (!Number.isFinite(lat0) || !Number.isFinite(lon0)) return;

    const records = Object.values(mapReport.data.recordsMap || {});
    const positions: [number, number][] = [];
    for (let i = 0; i < records.length; i += 1) {
      const rec = records[i];
      const x = Number(rec?.X ?? rec?.x);
      const y = Number(rec?.Y ?? rec?.y);
      if (Number.isFinite(x) && Number.isFinite(y)) {
        positions.push([lat0 + y / rangePerDegree.lat, lon0 + x / rangePerDegree.lon]);
      }
    }

    if (positions.length === 0) return;

    if (savedViewState && suppressedForRef.current !== facilityKey) {
      suppressedForRef.current = facilityKey;
      lastFitKeyRef.current = search;
      return;
    }

    // Refit only when the URL query changes (uid / facility / filter).
    // Pagination, refetches with the same query, and manual user pan/zoom are preserved.
    if (lastFitKeyRef.current === search) return;

    if (positions.length === 1) {
      map.setView(positions[0], SINGLE_POINT_ZOOM, { animate: false });
    } else {
      map.fitBounds(L.latLngBounds(positions), { padding: PADDING, animate: false });
    }
    lastFitKeyRef.current = search;
  }, [
    mapReport.data.recordsMap,
    mapReport.loading,
    mapOrigin.data,
    mapOrigin.loading,
    rangePerDegree,
    savedViewState,
    facilityKey,
    search,
    map,
  ]);

  return null;
};
